import React, { useState } from 'react';
import { useLocation } from 'react-router-dom'
import { FaCreditCard, FaPaypal, FaApplePay, FaCheck, FaArrowLeft } from 'react-icons/fa';

// Tier prices from CartPage.js
const tierPrices = {
    basic: 999,
    signature: 1499,
    luxury: 3499,
};

// Tier image mapping
const tierImageMap = {
    basic: 'https://www.foodies.pk/wp-content/uploads/2019/07/basic-free-package.png',
    signature: 'https://cdn5.vectorstock.com/i/1000x1000/90/29/vip-member-premium-user-icon-on-white-vector-34649029.jpg',
    luxury: 'https://media.istockphoto.com/id/1172210091/vector/diamond-line-icon-editable-stroke-pixel-perfect-for-mobile-and-web.jpg?s=612x612&w=0&k=20&c=fBTrpqRguqTxtcMdJ_7nTT16-zVtbDRmtX0XVeZSypg=',
};

// Generate QR Code
const generateQRCode = (text) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(text)}`;
};

export default function CheckoutPage() {
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState('form'); // 'form', 'payment', 'success'
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        paymentMethod: 'credit',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        nameOnCard: ''
    });
    const [cartData] = useState({
        selectedTier: location.state?.selectedTier || 'Basic',
        tierCost: location.state?.tierCost || 999,
        cartItems: location.state?.cartItems || [],
        totalCost: location.state?.totalCost || 999
    });
    const [showQR, setShowQR] = useState(false);
    const [orderNumber, setOrderNumber] = useState('');
    

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        // Basic validation
        const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
        const missingFields = requiredFields.filter(field => !formData[field].trim());

        if (missingFields.length > 0) {
            alert(`Please fill in required fields: ${missingFields.join(', ')}`);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }

        setCurrentStep('payment');
    };

    const handlePayment = async () => {
        // Validate payment fields
        if (formData.paymentMethod === 'credit') {
            if (!formData.cardNumber || !formData.expiryDate || !formData.cvv || !formData.nameOnCard) {
                alert('Please fill in all payment details');
                return;
            }
        }

        setIsProcessing(true);

        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Generate order number
        const orderNum = 'CGA-' + Date.now().toString().slice(-8);
        setOrderNumber(orderNum);

        setIsProcessing(false);
        setCurrentStep('success');
    };

    const PaymentMethodIcon = ({ method }) => {
        switch (method) {
            case 'credit': return <FaCreditCard style={{ marginRight: '8px' }} />;
            case 'paypal': return <FaPaypal style={{ marginRight: '8px' }} />;
            case 'apple': return <FaApplePay style={{ marginRight: '8px' }} />;
            default: return <FaCreditCard style={{ marginRight: '8px' }} />;
        }
    };

    // Success Page
    if (currentStep === 'success') {
        return (
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '48px',
                    textAlign: 'center'
                }}>
                    <div style={{ marginBottom: '32px' }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '80px',
                            height: '80px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            borderRadius: '50%',
                            marginBottom: '24px'
                        }}>
                            <FaCheck size={32} />
                        </div>
                        <h2 style={{ color: '#10b981', marginBottom: '16px', fontSize: '2.5rem', fontWeight: 'bold' }}>
                            Payment Successful!
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
                            Thank you for your purchase. Your booking has been confirmed.
                        </p>
                    </div>

                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '32px',
                        borderRadius: '8px',
                        marginBottom: '32px',
                        textAlign: 'left'
                    }}>
                        <h5 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px', textAlign: 'center' }}>
                            Order Details
                        </h5>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: '500' }}>Order Number:</span>
                                <span>{orderNumber}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: '500' }}>Total Amount:</span>
                                <span>${cartData.totalCost}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: '500' }}>Package:</span>
                                <span>{cartData.selectedTier} Tier</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: '500' }}>Customer:</span>
                                <span>{formData.firstName} {formData.lastName}</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <button
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontSize: '1.1rem',
                                fontWeight: '500',
                                marginRight: '16px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                            onClick={() => setShowQR(true)}
                        >
                            View Confirmation QR Code
                        </button>
                        <button
                            style={{
                                backgroundColor: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontSize: '1.1rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
                            onClick={() => setCurrentStep('form')}
                        >
                            Back to Shopping
                        </button>
                    </div>

                    <div style={{
                        backgroundColor: '#dbeafe',
                        border: '1px solid #3b82f6',
                        borderRadius: '8px',
                        padding: '16px'
                    }}>
                        <small style={{ color: '#1d4ed8' }}>
                            Confirmation email has been sent to {formData.email}
                            <br />
                            Please save your order number for future reference
                        </small>
                    </div>
                </div>

                {/* QR Code Modal */}
                {showQR && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            maxWidth: '400px',
                            width: '90%',
                            margin: '0 16px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '16px',
                                borderBottom: '1px solid #e5e7eb'
                            }}>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                                    Order Confirmation QR Code
                                </h3>
                                <button
                                    onClick={() => setShowQR(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.5rem',
                                        color: '#6b7280',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <div style={{ padding: '24px', textAlign: 'center' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <img
                                        src={generateQRCode(`Order: ${orderNumber}, Amount: $${cartData.totalCost}, Tier: ${cartData.selectedTier}, Customer: ${formData.firstName} ${formData.lastName}`)}
                                        alt="Order QR Code"
                                        style={{ maxWidth: '200px', height: 'auto' }}
                                    />
                                </div>
                                <p><strong>Order Number:</strong> {orderNumber}</p>
                                <p><strong>Total Amount:</strong> ${cartData.totalCost}</p>
                                <div style={{
                                    backgroundColor: '#d1fae5',
                                    border: '1px solid #10b981',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginTop: '16px'
                                }}>
                                    <small style={{ color: '#065f46' }}>
                                        Please save this QR code as payment confirmation
                                        <br />
                                        Can be used for on-site verification or customer service inquiries
                                    </small>
                                </div>
                            </div>
                            <div style={{
                                padding: '16px',
                                borderTop: '1px solid #e5e7eb',
                                textAlign: 'right'
                            }}>
                                <button
                                    style={{
                                        backgroundColor: '#6b7280',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowQR(false)}
                                >
                                    Close
                                </button>
                                <button
        style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            cursor: 'pointer'
        }}
        onClick={() => window.location.href = `https://citygetaway-frontend.vercel.app/`}
    >
        Go to FIT Page
    </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Payment Page
    if (currentStep === 'payment') {
        return (
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
                <button
                    style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        marginBottom: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#374151'
                    }}
                    onClick={() => setCurrentStep('form')}
                >
                    <FaArrowLeft style={{ marginRight: '8px' }} />
                    Back to Information
                </button>

                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{
                        padding: '16px 24px',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <h4 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                            Confirm Payment
                        </h4>
                    </div>
                    <div style={{ padding: '24px' }}>
                        {/* Order Summary */}
                        <div style={{ marginBottom: '32px' }}>
                            <h5 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px' }}>
                                Order Summary
                            </h5>
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span>{cartData.selectedTier} Tier</span>
                                    <span>${cartData.tierCost}</span>
                                </div>
                                {cartData.cartItems.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>{item.name}</span>
                                        <span>${item.cost}</span>
                                    </div>
                                ))}
                                <hr style={{ margin: '12px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                    <span>Total</span>
                                    <span>${cartData.totalCost}</span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Methods */}
                        <div style={{ marginBottom: '32px' }}>
                            <h5 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px' }}>
                                Payment Method
                            </h5>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                {[
                                    { key: 'credit', label: 'Credit Card', icon: 'credit' },
                                    { key: 'paypal', label: 'PayPal', icon: 'paypal' },
                                    { key: 'apple', label: 'Apple Pay', icon: 'apple' }
                                ].map(method => (
                                    <button
                                        key={method.key}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            border: formData.paymentMethod === method.key ? '2px solid #3b82f6' : '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            backgroundColor: formData.paymentMethod === method.key ? '#eff6ff' : 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: method.key }))}
                                    >
                                        <PaymentMethodIcon method={method.icon} />
                                        {method.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Payment Form */}
                        {formData.paymentMethod === 'credit' && (
                            <div style={{ marginBottom: '32px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                        Name on Card *
                                    </label>
                                    <input
                                        type="text"
                                        name="nameOnCard"
                                        value={formData.nameOnCard}
                                        onChange={handleInputChange}
                                        placeholder="John Doe"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '16px'
                                        }}
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                        Card Number *
                                    </label>
                                    <input
                                        type="text"
                                        name="cardNumber"
                                        value={formData.cardNumber}
                                        onChange={handleInputChange}
                                        placeholder="1234 5678 9012 3456"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '16px'
                                        }}
                                        required
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                            Expiry Date *
                                        </label>
                                        <input
                                            type="text"
                                            name="expiryDate"
                                            value={formData.expiryDate}
                                            onChange={handleInputChange}
                                            placeholder="MM/YY"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '16px'
                                            }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                            CVV *
                                        </label>
                                        <input
                                            type="text"
                                            name="cvv"
                                            value={formData.cvv}
                                            onChange={handleInputChange}
                                            placeholder="123"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '16px'
                                            }}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Other payment methods placeholder */}
                        {formData.paymentMethod === 'paypal' && (
                            <div style={{
                                padding: '24px',
                                backgroundColor: '#fef3c7',
                                borderRadius: '8px',
                                marginBottom: '32px',
                                textAlign: 'center'
                            }}>
                                <p>You will be redirected to PayPal to complete your payment.</p>
                            </div>
                        )}

                        {formData.paymentMethod === 'apple' && (
                            <div style={{
                                padding: '24px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '8px',
                                marginBottom: '32px',
                                textAlign: 'center'
                            }}>
                                <p>Use Touch ID or Face ID to pay with Apple Pay.</p>
                            </div>
                        )}

                        {/* Confirm Payment Button */}
                        <button
                            style={{
                                width: '100%',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '16px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                            onClick={handlePayment}
                            disabled={isProcessing}
                        >
                            
                            {isProcessing ? (
                                <>
                                
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        border: '2px solid transparent',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        marginRight: '8px'
                                    }}></div>
                                    Processing...
                                </>
                            ) : (
                                `Confirm Payment $${cartData.totalCost}`
                            )}
                        </button>
                    </div>
                </div>

                {/* CSS for spinner animation */}
                <style>
                    {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                </style>
            </div>
        );
    }

    // Form Page (Customer Information)
    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <h4 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                        Customer Information
                    </h4>
                </div>
                <div style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '32px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '16px'
                                    }}
                                    required
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '16px'
                                    }}
                                    required
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                Email Address *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '16px'
                                }}
                                required
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                Phone Number *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '16px'
                                }}
                                required
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                Address
                            </label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '16px'
                                }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                    City
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '16px'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                                    Country
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '16px'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '24px',
                        borderRadius: '8px',
                        marginBottom: '24px'
                    }}>
                        <h5 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px' }}>
                            Order Summary
                        </h5>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span>{cartData.selectedTier} Tier</span>
                            <span>${cartData.tierCost}</span>
                        </div>
                        {cartData.cartItems.map((item, index) => (
                            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>{item.name}</span>
                                <span>${item.cost}</span>
                            </div>
                        ))}
                        <hr style={{ margin: '12px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem' }}>
                            <span>Total</span>
                            <span>${cartData.totalCost}</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        style={{
                            width: '100%',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '16px',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                        onClick={handleFormSubmit}
                    >
                        Continue to Payment
                    </button>
                </div>
            </div>
        </div>
    );
}